"use client";

import "./App.css";
import { useState, useEffect } from "react";
import Vapi from "@vapi-ai/web";
import { Phone, PhoneOff, Mic, Volume2, MicOff } from "lucide-react";

const vapi = new Vapi("9218eb39-bc58-4da2-bcfb-6b4fc4a17bab");

function App() {
  // const [data, setData] = useState(null);
  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       const response = await fetch(
  //         "https://backend-466519766426.us-central1.run.app/user/67c54e3cd3ae9715f4875d48"
  //       );
  //       if (!response.ok) {
  //         throw new Error("Network response was not ok");
  //       }
  //       const result = await response.json();
  //       setData(result);
  //     } catch (error) {
  //       console.error("Error fetching data:", error);
  //     }
  //   };

  //   fetchData();
  // }, []);

  // const userDetails = {
  //   name: data?.data?.name || "Not Provided",
  //   "Elevation Certificate":
  //     data?.data?.ec_certificate_name === "" ? "Uploaded" : "Not uploaded",
  //   Policy: data?.data?.ec_policy_name === "" ? "Uploaded" : "Not uploaded",
  // };

  const [content, setContent] =
    useState(`You are a friendly and helpful Starbucks virtual assistant with a warm, welcoming female voice. Your goal is to take coffee orders from customers just like a real Starbucks barista.

Start by greeting the customer with enthusiasm and introduce yourself. Ask for their name naturally to create a personalized experience. Guide them through the Starbucks menu, suggesting popular drinks and customization options (like size, milk type, and additional flavors).

After confirming their order, politely ask for their delivery address and preferred payment method. Make small talk when appropriate, such as complimenting their choice or asking if they’d like to add a snack.

Always maintain a cheerful, human-like tone, ensuring a smooth and friendly ordering experience. Once the order is complete, summarize it and give an estimated delivery time before ending the conversation on a positive note.`);
  const [callActive, setCallActive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [callLogs, setCallLogs] = useState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    vapi.on("speech-start", () => {
      setIsSpeaking(true);
      addLog("Assistant started speaking");
    });

    vapi.on("speech-end", () => {
      setIsSpeaking(false);
      addLog("Assistant finished speaking");
    });

    vapi.on("call-start", () => {
      setIsConnecting(false);
      setCallActive(true);
      addLog("Call started");
      vapi.send({
        type: "add-message",
        message: {
          role: "system",
          content: "Hello! Welcome to FlowVoice. How can I assist you today?",
        },
      });
    });

    vapi.on("call-end", () => {
      setCallActive(false);
      setIsSpeaking(false);
      setVolumeLevel(0);
      setIsConnecting(false);
      setIsMuted(false);
      addLog("Call ended");
    });

    vapi.on("volume-level", (volume) => {
      setVolumeLevel(volume * 100);
    });

    return () => {
      vapi.removeAllListeners();
    };
  }, []);

  const addLog = (message) => {
    setCallLogs((prev) => [
      ...prev,
      {
        timestamp: new Date().toLocaleTimeString(),
        message,
      },
    ]);
  };

  const startCall = () => {
    setCallLogs([]);
    setIsConnecting(true);
    vapi.start({
      transcriber: {
        provider: "deepgram",
        model: "nova-3",
        language: "en-US",      
      },
      model: {
        provider: "openai",
        model: "gpt-4o-mini",    
        messages: [
          {
            role: "system",
            content: content,
          },
        ],
      },
      voice: {
        provider: "openai",
        voiceId: "nova",      
      },
      name: "Westack AI Assistant",
      firstMessageMode:"assistant-speaks-first"
    });
  };

  const endCall = () => {
    vapi?.stop();
  };

  const toggleMute = () => {
    if (callActive) {
      if (isMuted) {
        vapi.setMuted(false);
        addLog("Microphone unmuted");
      } else {
        vapi.setMuted(true);
        addLog("Microphone muted");
      }
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="app-container">
      <div className="main-content">
        <div className="control-panel">
          <div className="panel-header">
            <h2 className="panel-title">
              <Mic className="icon" />
              Voice Assistant Configuration
            </h2>
          </div>
          <div className="panel-content">
            <div className="textarea-container">
              <label>Assistant Context</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter the context and instructions for your AI assistant..."
                disabled={callActive}
                rows={16}
              />
            </div>

            <div className="status-indicators">
              <div className="status-box">
                <div className="status-header">
                  <Phone className="icon-small" />
                  <span>Call Status</span>
                </div>
                <div className={`status-text ${callActive ? "active" : ""}`}>
                  {isConnecting
                    ? "Connecting..."
                    : callActive
                    ? "Active"
                    : "Inactive"}
                </div>
              </div>
              <div className="status-box">
                <div className="status-header">
                  <Volume2 className="icon-small" />
                  <span>Volume Level</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${volumeLevel}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="control-buttons">
              <button
                className={`btn start-btn ${callActive ? "disabled" : ""} ${
                  isConnecting ? "connecting" : ""
                }`}
                onClick={startCall}
                disabled={callActive}
              >
                <Phone className="icon-small" />
                {isConnecting ? "Connecting..." : "Start Call"}
              </button>
              <button
                className={`btn end-btn ${!callActive ? "disabled" : ""}`}
                onClick={endCall}
                disabled={!callActive}
              >
                <PhoneOff className="icon-small" />
                End Call
              </button>
              <button
                className={`btn mute-btn ${!callActive ? "disabled" : ""} ${
                  isMuted ? "muted" : ""
                }`}
                onClick={toggleMute}
                disabled={!callActive}
              >
                {isMuted ? (
                  <MicOff className="icon-small" />
                ) : (
                  <Mic className="icon-small" />
                )}
              </button>
            </div>

            {isSpeaking && (
              <div className="speaking-indicator">
                <span className="pulse-dot"></span>
                Assistant is speaking
              </div>
            )}
          </div>
        </div>

        <div className="activity-log">
          <div className="panel-header">
            <h2 className="panel-title">Activity Log</h2>
          </div>
          <div className="log-content">
            {callLogs.map((log, index) => (
              <div key={index} className="log-entry">
                <span className="timestamp">{log.timestamp}</span>
                <p className="message">{log.message}</p>
              </div>
            ))}
            {callLogs.length === 0 && (
              <p className="no-activity">No activity yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
